import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PercentageCalculator = () => {
  const [calculatorType, setCalculatorType] = useState('percentage-of');
  const [inputs, setInputs] = useState({
    value: '',
    total: '',
    percentage: '',
    startValue: '',
    endValue: ''
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePercentage = () => {
    const { value, total, percentage, startValue, endValue } = inputs;
    let calculationResult = null;
    let chartResult = null;
    let resultMessage = '';

    try {
      switch (calculatorType) {
        case 'percentage-of':
          // Calculate percentage of a number
          if (!value || !total) {
            throw new Error('Please enter both value and total');
          }
          if (isNaN(value) || isNaN(total)) {
            throw new Error('Please enter valid numbers');
          }
          const numericValue = parseFloat(value);
          const numericTotal = parseFloat(total);
          
          if (numericTotal <= 0) {
            throw new Error('Total must be greater than zero');
          }
          
          const result = (numericValue / numericTotal) * 100;
          calculationResult = {
            value: numericValue,
            total: numericTotal,
            percentage: result.toFixed(2),
            message: `${numericValue} is ${result.toFixed(2)}% of ${numericTotal}`
          };

          // Create visualization data
          chartResult = {
            labels: ['Value', 'Remaining'],
            datasets: [
              {
                data: [numericValue, numericTotal - numericValue],
                backgroundColor: [
                  'rgba(54, 162, 235, 0.7)',
                  'rgba(230, 230, 230, 0.7)'
                ],
                borderColor: [
                  'rgba(54, 162, 235, 1)',
                  'rgba(230, 230, 230, 1)'
                ],
                borderWidth: 1,
              }
            ]
          };
          break;

        case 'value-of-percentage':
          // Calculate value from percentage
          if (!percentage || !total) {
            throw new Error('Please enter both percentage and total');
          }
          if (isNaN(percentage) || isNaN(total)) {
            throw new Error('Please enter valid numbers');
          }
          const numericPercentage = parseFloat(percentage);
          const numericTotalValue = parseFloat(total);
          
          if (numericTotalValue <= 0) {
            throw new Error('Total must be greater than zero');
          }
          
          const valueResult = (numericPercentage / 100) * numericTotalValue;
          calculationResult = {
            percentage: numericPercentage,
            total: numericTotalValue,
            value: valueResult.toFixed(2),
            message: `${numericPercentage}% of ${numericTotalValue} is ${valueResult.toFixed(2)}`
          };

          // Create visualization data
          chartResult = {
            labels: ['Calculated Value', 'Remaining'],
            datasets: [
              {
                data: [valueResult, numericTotalValue - valueResult],
                backgroundColor: [
                  'rgba(75, 192, 192, 0.7)',
                  'rgba(230, 230, 230, 0.7)'
                ],
                borderColor: [
                  'rgba(75, 192, 192, 1)',
                  'rgba(230, 230, 230, 1)'
                ],
                borderWidth: 1,
              }
            ]
          };
          break;

        case 'percentage-change':
          // Calculate percentage increase/decrease
          if (!startValue || !endValue) {
            throw new Error('Please enter both start and end values');
          }
          if (isNaN(startValue) || isNaN(endValue)) {
            throw new Error('Please enter valid numbers');
          }
          const numericStart = parseFloat(startValue);
          const numericEnd = parseFloat(endValue);
          
          if (numericStart === 0) {
            throw new Error('Start value cannot be zero');
          }
          
          const change = numericEnd - numericStart;
          const percentChange = (change / numericStart) * 100;
          const changeType = percentChange >= 0 ? 'increase' : 'decrease';
          
          calculationResult = {
            start: numericStart,
            end: numericEnd,
            change: change.toFixed(2),
            percentChange: Math.abs(percentChange).toFixed(2),
            type: changeType,
            message: `From ${numericStart} to ${numericEnd} is a ${Math.abs(percentChange).toFixed(2)}% ${changeType}`
          };

          // Create visualization data
          chartResult = {
            labels: ['Start Value', 'End Value'],
            datasets: [
              {
                label: 'Values',
                data: [numericStart, numericEnd],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.7)',
                  'rgba(54, 162, 235, 0.7)'
                ],
                borderColor: [
                  'rgba(255, 99, 132, 1)',
                  'rgba(54, 162, 235, 1)'
                ],
                borderWidth: 1,
              }
            ]
          };
          break;

        default:
          throw new Error('Invalid calculator type');
      }

      setResult(calculationResult);
      setChartData(chartResult);
    } catch (err) {
      setError(err.message);
      setResult(null);
      setChartData(null);
    }
  };

  const exampleData = {
    percentageOf: {
      value: '45',
      total: '300',
      percentage: '15'
    },
    valueOfPercentage: {
      percentage: '20',
      total: '250',
      value: '50'
    },
    percentageChange: {
      startValue: '150',
      endValue: '225',
      percentChange: '50'
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-yellow-100 to-orange-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Percentage Calculator | Multi-Functional Calculator</title>
          <meta name="description" content="Calculate percentages with this versatile calculator. Find percentage of a number, value from percentage, or percentage increase/decrease." />
          <meta name="keywords" content="percentage calculator, calculate percentage, percentage increase, percentage decrease, value from percentage" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-yellow-600 mr-4">📊</div>
            <h1 className="text-3xl font-bold text-gray-800">Percentage Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>A versatile calculator for all your percentage needs. Calculate percentages of values, find values from percentages, or determine percentage changes between two values.</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Type:</label>
            <div className="flex space-x-4">
              <button
                onClick={() => setCalculatorType('percentage-of')}
                className={`py-2 px-4 rounded-lg ${calculatorType === 'percentage-of' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'} flex items-center`}
              >
                What is % of a number?
              </button>
              <button
                onClick={() => setCalculatorType('value-of-percentage')}
                className={`py-2 px-4 rounded-lg ${calculatorType === 'value-of-percentage' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'} flex items-center`}
              >
                Value from percentage
              </button>
              <button
                onClick={() => setCalculatorType('percentage-change')}
                className={`py-2 px-4 rounded-lg ${calculatorType === 'percentage-change' ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'} flex items-center`}
              >
                Percentage increase/decrease
              </button>
            </div>
          </div>

          {calculatorType === 'percentage-of' && (
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Value:</label>
                <input
                  type="text"
                  name="value"
                  value={inputs.value}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter value (e.g., 45)"
                />
              </div>
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Total:</label>
                <input
                  type="text"
                  name="total"
                  value={inputs.total}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter total (e.g., 300)"
                />
              </div>
            </div>
          )}

          {calculatorType === 'value-of-percentage' && (
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Percentage:</label>
                <input
                  type="text"
                  name="percentage"
                  value={inputs.percentage}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter percentage (e.g., 20)"
                />
              </div>
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Total:</label>
                <input
                  type="text"
                  name="total"
                  value={inputs.total}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter total (e.g., 250)"
                />
              </div>
            </div>
          )}

          {calculatorType === 'percentage-change' && (
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Value:</label>
                <input
                  type="text"
                  name="startValue"
                  value={inputs.startValue}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter start value (e.g., 150)"
                />
              </div>
              <div className="input-group">
                <label className="block text-sm font-medium text-gray-700 mb-2">End Value:</label>
                <input
                  type="text"
                  name="endValue"
                  value={inputs.endValue}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Enter end value (e.g., 225)"
                />
              </div>
            </div>
          )}

          <button
            onClick={calculatePercentage}
            className="w-full py-3 px-4 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  {result.message}
                </h3>
                {calculatorType === 'percentage-change' && (
                  <p className={`text-lg mt-2 font-bold ${result.type === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                    {result.type === 'increase' ? '👍' : '👎'} {result.type}
                  </p>
                )}
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  {calculatorType === 'percentage-change' ? (
                    <Bar
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Values'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Comparison'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Value Comparison'
                          }
                        }
                      }}
                    />
                  ) : (
                    <Pie
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          title: {
                            display: true,
                            text: calculatorType === 'percentage-of' 
                              ? 'Value Distribution' 
                              : 'Percentage Distribution'
                          },
                          legend: {
                            position: 'bottom'
                          }
                        }
                      }}
                    />
                  )}
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                <div className="space-y-4">
                  {calculatorType === 'percentage-of' && (
                    <>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Formula: (Value ÷ Total) × 100 = Percentage
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Calculation: ({result.value} ÷ {result.total}) × 100 = {result.percentage}%
                        </p>
                      </div>
                    </>
                  )}

                  {calculatorType === 'value-of-percentage' && (
                    <>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Formula: (Percentage × Total) ÷ 100 = Value
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Calculation: ({result.percentage} × {result.total}) ÷ 100 = {result.value}
                        </p>
                      </div>
                    </>
                  )}

                  {calculatorType === 'percentage-change' && (
                    <>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Formula: ((End - Start) ÷ Start) × 100 = Percentage Change
                        </p>
                      </div>
                      <div className="p-4 bg-white rounded-lg shadow-inner">
                        <p className="text-gray-700">
                          Calculation: (({result.end} - {result.start}) ÷ {result.start}) × 100 = {result.percentChange}%
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculations <span className="text-yellow-600">📝</span></h2>
          
          <div className="bg-white p-6 rounded-lg shadow-md mt-4">
            <h4 className="mb-4">What is % of a number?</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Value: {exampleData.percentageOf.value}</li>
              <li>Total: {exampleData.percentageOf.total}</li>
              <li>Result: {exampleData.percentageOf.percentage}%</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mt-4">
            <h4 className="mb-4">Value from percentage:</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Percentage: {exampleData.valueOfPercentage.percentage}%</li>
              <li>Total: {exampleData.valueOfPercentage.total}</li>
              <li>Result: {exampleData.valueOfPercentage.value}</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mt-4">
            <h4 className="mb-4">Percentage increase/decrease:</h4>
            <ul className="list-disc pl-6 mb-4">
              <li>Start Value: {exampleData.percentageChange.startValue}</li>
              <li>End Value: {exampleData.percentageChange.endValue}</li>
              <li>Result: {exampleData.percentageChange.percentChange}% increase</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-yellow-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Percentage Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Calculation Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose from three calculation types</li>
                </ul>
              </li>
              <li><strong>Enter Values:</strong> Input the required numbers based on your selected calculation type</li>
              <li><strong>Click Calculate:</strong> Get instant results with visualization</li>
              <li><strong>Review Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>View the calculated percentage or value</li>
                  <li>Check the calculation breakdown</li>
                  <li>Visualize the results with charts</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-yellow-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use for financial calculations like interest rates and discounts</li>
                <li>Calculate grade percentages</li>
                <li>Determine tax amounts</li>
                <li>Track performance improvements with percentage change</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Percentages <span className="text-yellow-600">📊</span></h2>
          <div className="text-gray-600">
            <p>
              Percentages are a way of expressing a number as a fraction of 100. They are useful because:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Standardization:</strong> Provides a common scale for comparison</li>
              <li><strong>Intuitive:</strong> Easier to understand than fractions or decimals for most people</li>
              <li><strong>Visualization:</strong> Helps represent data in charts and graphs</li>
              <li><strong>Everyday Applications:</strong> Used in finance, statistics, education, and more</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-yellow-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Context Matters:</strong> Always consider the context when interpreting percentages</li>
              <li><strong>Baseline Important:</strong> Pay attention to the base value when calculating percentages</li>
              <li><strong>Avoid Cumulative Misuse:</strong> Percentages don't add up linearly in cumulative calculations</li>
              <li><strong>Round Appropriately:</strong> Use appropriate decimal places based on precision needs</li>
            </ul>
            <p className="italic mt-4">
              Note: When comparing percentages, ensure you're using the same base values for accurate comparisons.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PercentageCalculator;